import { useEffect, useMemo, useRef, useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import { BsChevronDown } from 'react-icons/bs';
import { useSelector } from 'react-redux';
import styled, { keyframes } from 'styled-components';
import { media } from '../../helpers/responsive';
import LocationPin from '../../images/icons/locationPin.svg';
import { type RootState, dispatchAction } from '../../state/store';
import { LocationFilterProvider } from './context';
import MultiSelectMenu from './multiSelectMenu';
import { filterLocationsBySelectedDateRange } from '../../helpers/utils';
import { clearLocationFilterFocus } from '../../state/features/analytics/actions';

const LocationFilterDropdown = () => {
  const appliedLocationFilters = useSelector((state: RootState) => state.filters.locations);

  const companyLocations = useSelector((state: RootState) => state.locations);
  const selectedDateRange = useSelector((state: RootState) => state.filters.dates);
  const focusLocationFilters = useSelector(
    (state: RootState) => state.analytics.focusFilter.location
  );

  const locationsValidInSelectedDateRange = useMemo(() => {
    return filterLocationsBySelectedDateRange(companyLocations, selectedDateRange);
  }, [JSON.stringify(selectedDateRange), JSON.stringify(companyLocations)]);

  const [isMenuOpen, setMenuOpen] = useState(false);

  const toggleRef = useRef<HTMLDivElement>(null);

  const toggleTitle = useMemo(() => {
    let title = '';

    if (locationsValidInSelectedDateRange.length > 0) {
      if (appliedLocationFilters.length === locationsValidInSelectedDateRange.length) {
        title = 'All Locations';
      } else if (appliedLocationFilters.length > 1) {
        title = 'Multiple Locations';
      } else {
        const location = locationsValidInSelectedDateRange.find(
          (d: any) => d.id === appliedLocationFilters[0]
        );
        title = location?.name || 'No locations';
      }
    } else {
      title = 'No locations';
    }

    // System-generated titles should never be truncated
    const systemTitles = ['Multiple Locations', 'All Locations', 'No locations'];
    if (systemTitles.includes(title)) {
      return title;
    }

    // Truncate to 50 characters with ellipsis for user-defined location names only
    return title.length > 50 ? title.substring(0, 47) + '...' : title;
  }, [locationsValidInSelectedDateRange, appliedLocationFilters]);

  const handleDropdownToggle = (show: boolean) => {
    setMenuOpen(show);
  };

  useEffect(() => {
    if (!isMenuOpen && focusLocationFilters) {
      setTimeout(() => {
        toggleBtnRef.current?.click();
      }, 500);
    }
    if (isMenuOpen && focusLocationFilters) {
      dispatchAction(clearLocationFilterFocus());
    }
  }, [focusLocationFilters, isMenuOpen]);

  const toggleBtnRef = useRef<HTMLButtonElement>(null);

  return (
    <Wrapper className="location-select-control">
      <LocationFilterProvider isMenuOpen={isMenuOpen} dropdownBtnRef={toggleRef}>
        <Dropdown onToggle={handleDropdownToggle} ref={toggleRef}>
          <Dropdown.Toggle ref={toggleBtnRef}>
            <img src={LocationPin} alt="location" /> <p>{toggleTitle}</p>
            <BsChevronDown width={'14px'} />
          </Dropdown.Toggle>
          <Dropdown.Menu>
            <MultiSelectMenu />
          </Dropdown.Menu>
        </Dropdown>
      </LocationFilterProvider>
    </Wrapper>
  );
};

export default LocationFilterDropdown;

const fadeInTop = keyframes`
  0% {
    -webkit-transform: translateY(-1px);
    transform: translateY(-1px);
    opacity: 0;
  }
  100% {
    -webkit-transform: translateY(-50);
    transform: translateY(-50);
    opacity: 1;
  }
`;

const Wrapper = styled.div`
  .dropdown {
    .dropdown-toggle::after {
      content: none;
    }
    .dropdown-toggle {
      display: flex;
      flex-direction: row;
      justify-content: start;
      align-items: center;
      width: auto; /* Auto-resize to fit content */
      min-width: fit-content;
      max-width: 280px; /* Maximum width to prevent extremely long names from breaking layout */
      > img {
        position: relative;
        width: 25px;
        flex-shrink: 0;
      }
      > p {
        margin-bottom: 0px;
        margin-left: 10px;
        margin-right: 1rem;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        flex: 0 1 auto; /* Allow text to shrink but not grow beyond content */
        font-size: 14px;
      }
      > svg {
        flex-shrink: 0;
      }
      &.btn-primary {
        background-color: ${({ theme }) => theme.widget.background};
        border-color: ${({ theme }) => theme.widget.background};
        color: ${({ theme }) => theme.text};
        border-radius: 8px;
        &:focus {
          box-shadow: none;
        }
      }
    }
    .dropdown-menu {
      animation: ${fadeInTop} 0.2s;
      min-width: 350px;
      padding: 20px;
      border: 1px solid ${({ theme }) => theme.border_color};
      box-shadow: 0px 14px 30px rgba(0, 0, 0, 0.1);
      border-radius: 20px;

      .location-select__control {
        cursor: text;
        border: 1px solid ${({ theme }) => theme.border_color};
        &.location-select__control--is-focused {
          border-color: #0d8a6a;
          box-shadow: none;
        }
      }
      .location-select__menu {
        border-radius: 0px 0px 20px 20px;
        position: static;
        box-shadow: none;
        margin-bottom: 0;
        .hide-select-all {
          display: none;
        }
        .select_all_option {
          cursor: pointer;
          border-bottom: 1px solid ${({ theme }) => theme.border_color};
          padding: 0.5rem 0;
          font-size: 14px;
          &:hover {
            color: #fff;
            background-color: #3aa98d;
            font-weight: normal;
          }
        }
        .dropdown-actions {
          display: flex;
          margin-top: 0.5rem;
          .btn {
            padding: 5px 0;
            width: 100%;
            height: 40px;
            font-weight: 500;
            font-size: 14px;
            border-radius: 4px;
            &:first-child {
              margin-right: 10px;
            }
          }
          .cancel-btn {
            border: 1px solid #1f1f1f;
            background-color: ${({ theme }) => theme.body};
            color: ${({ theme }) => theme.text};
          }
          .submit-btn {
            border: none;
            background-color: rgb(13, 138, 106);
          }
        }
        .location-select__menu-list {
          padding: 0;
          .location-select__option {
            cursor: pointer;
            font-size: 14px;
            &:hover {
              color: #fff;
              background-color: #3aa98d;
              font-weight: normal;
            }
            &.location-select__option--is-selected {
              color: ${({ theme }) => theme.dropdown.color};
              background: ${({ theme }) => theme.dropdown.selected};
              &:hover {
                background-color: #3aa98d;
                font-weight: normal;
              }
            }

            .hierarchical-option {
              display: flex;
              align-items: center;
              gap: 8px;
              width: 100%;

              &.parent-option {
                font-weight: 500;
              }

              &.child-option {
                padding-left: 0px;
                font-size: 14px;
              }

              .option-label {
                flex: 1;
                min-width: 0;
              }

              .expand-arrow {
                cursor: pointer;
                display: inline-flex;
                align-items: center;
                justify-content: center;
                user-select: none;
                flex-shrink: 0;
                margin-left: auto;
                padding: 4px;
                color: ${({ theme }) => theme.text};

                &:hover {
                  opacity: 0.7;
                }
              }

              .child-indent {
                width: 15px;
                flex-shrink: 0;
              }

              svg {
                flex-shrink: 0;
              }

              .partial-checkbox {
                color: ${({ theme }) => theme.text};
                opacity: 1;
                width: 17.5px !important;
                height: 17.5px !important;
                font-size: 17.5px;
                padding: 6.25px;
                display: inline-flex;
                align-items: center;
                justify-content: center;
                box-sizing: content-box;
                stroke-width: 2;

                svg {
                  stroke-width: 2px;
                }
              }
            }
          }
        }
      }
    }
  }
  ${media.down.lg} {
    .dropdown .dropdown-toggle {
      max-width: 250px;
      > img { width: 20px; }
      &.btn-primary { padding: 6px; }
    }
  }

  ${media.down.xs} {
    .dropdown {
      width: 100%;
      .dropdown-toggle {
        width: 100%;
        max-width: 100%;
        height: 40px;
        padding: 0 12px;
        > img {
          width: 22px;
          flex-shrink: 0;
        }
        > p {
          flex: 1;
          text-align: center;
          margin: 0 8px;
          font-size: 13px;
        }
        > svg {
          flex-shrink: 0;
          width: 14px !important;
          height: 14px !important;
        }
      }
      .dropdown-menu {
        max-width: calc(100vw - 32px);
        min-width: min(350px, calc(100vw - 32px));
      }
    }
  }
`;
